import React, { useState, useEffect } from "react";
import { FiInfo } from "react-icons/fi";
import { auth, db } from "../../firebase";
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import StaffChangePassword from "../../components/staffcomponents/StaffChangePassword";
import TwoFactorOtpModal from "../../components/otpmodals/TwoFactorOtpModal";
import StaffChangeVerificationModal from "../../components/otpmodals/StaffChangeVerificationModal";
import { useLanguage } from "../../contexts/LanguageContext";
import "./staffcss/sharedprofile.css";
import { useNavigate, useLocation } from "react-router-dom";
import { sessionService } from "../../services/sessionService";
import { apiRevokeSession, apiLogoutAllSessions } from "../../services/sessionApiService";

// Helper function to format Firestore timestamp to readable date
const formatTimestamp = (timestamp) => {
  if (!timestamp) return { date: "", time: "" };
  
  let date;
  // Check if it's a Firestore Timestamp
  if (timestamp && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  }
  // Check if it's a string that looks like a timestamp
  else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    return { date: "", time: "" };
  }
  
  if (isNaN(date.getTime())) return { date: "", time: "" };
  
  const dateStr = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase();
  return { date: dateStr, time: timeStr };
};

const MedicalProfile = () => {
  const { language, changeLanguage, getTranslations } = useLanguage();
  const t = getTranslations(false, false, false, true);
  const navigate = useNavigate();
  const location = useLocation();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [pendingTwoFactorValue, setPendingTwoFactorValue] = useState(false);
  const [showContactVerificationModal, setShowContactVerificationModal] = useState(false);
  const [pendingContactChange, setPendingContactChange] = useState(null);
  const [showPasswordVerificationModal, setShowPasswordVerificationModal] = useState(false);
  const [pendingPasswordChange, setPendingPasswordChange] = useState(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [tempPhone, setTempPhone] = useState("");
  const [tempLanguage, setTempLanguage] = useState("English");
  const [errors, setErrors] = useState({
    contact: ""
  });
  const [userData, setUserData] = useState({
    fullName: "",
    email: "",
    phone: "",
    dateCreated: "",
    lastLogin: "",
    language: "English"
  });

  // ---- Session management state ----
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokeLoadingId, setRevokeLoadingId] = useState(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [showLogoutAllConfirm, setShowLogoutAllConfirm] = useState(false);
  const currentSessionId = sessionService.getCurrentSessionId();

  const formatSessionTime = (date) => {
    if (!date) return "Unknown";
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Fetch user data when component mounts with real-time updates
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (userDoc) => {
      if (userDoc.exists()) {
        const data = userDoc.data();
        // Strip +63 prefix if present, keep only last 10 digits
        const phoneDigits = data.phone ? data.phone.replace(/\D/g, "").slice(-10) : "";
        setUserData({
          fullName: data.fullName || "",
          email: data.email || "",
          phone: phoneDigits,
          dateCreated: formatTimestamp(data.createdAt),
          lastLogin: formatTimestamp(data.lastLogin),
          language: data.language || "English"
        });

        // Fetch two-factor authentication state
        if (typeof data.twoFactorEnabled === "boolean") {
          setTwoFactorEnabled(data.twoFactorEnabled);
        } else {
          // Default to false if missing
          setTwoFactorEnabled(false);
        }
      }
    }, (error) => {
      console.error('Error fetching user data:', error);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const updateTwoFactor = async (nextValue) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      await updateDoc(doc(db, "users", user.uid), { 
        twoFactorEnabled: nextValue, 
        updatedAt: serverTimestamp() 
      });
      
      // Update local storage to keep user data in sync
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (storedUser.uid === user.uid) {
        localStorage.setItem("user", JSON.stringify({ ...storedUser, twoFactorEnabled: nextValue }));
      }
      
      setTwoFactorEnabled(nextValue);
    } catch (err) {
      console.error("Error updating 2FA:", err);
    }
  };

  const handleTwoFactorVerificationSuccess = async (verifiedValue) => {
    await updateTwoFactor(verifiedValue);
    setShowTwoFactorModal(false);
  };

  const handleContactVerificationSuccess = async (result) => {
    setIsEditingPhone(false);
    setShowContactVerificationModal(false);
    // Use the updated values from the verification result instead of re-reading the document
    if (result && result.phone) {
      // Strip +63 prefix if present, keep only last 10 digits
      const phoneDigits = result.phone.replace(/\D/g, "").slice(-10);
      setUserData({
        ...userData,
        phone: phoneDigits,
        language: result.language || userData.language
      });
    }
  };

  const handlePasswordVerificationRequired = (passwordData) => {
    setPendingPasswordChange(passwordData);
    setShowPasswordVerificationModal(true);
  };

  const handlePasswordVerificationSuccess = async (result) => {
    setShowPasswordVerificationModal(false);
    // Password change was successful, user will be signed out by the modal
    // Navigate to login page
    navigate('/login');
  };

  const validateContact = (phone) => {
    if (!phone.trim()) {
      return "Contact number is required";
    }
    if (!/^\d{10}$/.test(phone)) {
      return "Contact number must be 10 digits";
    }
    if (!phone.startsWith("9")) {
      return "Contact number must start with 9";
    }
    return "";
  };

  const handleContactChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    setTempPhone(digitsOnly);
    setErrors(prev => ({ ...prev, contact: validateContact(digitsOnly) }));
  };

  // ---- Session subscription & revocation detection ----
  // Use onAuthStateChanged instead of auth.currentUser so we wait for Firebase
  // to restore the authenticated state before attaching Firestore listeners.
  // auth.currentUser can be null on first render even when the user IS logged in
  // (Firebase hasn't finished rehydrating from persistence yet).
  useEffect(() => {
    let unsubSessions = null;
    let unsubRevocation = null;

    const unsubAuth = auth.onAuthStateChanged((firebaseUser) => {
      if (unsubSessions) { unsubSessions(); unsubSessions = null; }
      if (unsubRevocation) { unsubRevocation(); unsubRevocation = null; }

      if (!firebaseUser?.uid) return;
      const uid = firebaseUser.uid;

      setSessionsLoading(true);
      unsubSessions = sessionService.watchSessions(uid, (sessions) => {
        setActiveSessions(sessions);
        setSessionsLoading(false);
      });

      unsubRevocation = sessionService.watchCurrentSession(uid, async () => {
        const isLogoutInProgress = sessionStorage.getItem('logoutInProgress');
        if (isLogoutInProgress) return;
        sessionStorage.setItem('logoutInProgress', 'true');
        sessionService.clearCurrentSession();
        try { await signOut(auth); } catch (_) {}
        localStorage.clear();
        navigate('/login', { replace: true, state: { message: 'Your session has been revoked. Please sign in again.', type: 'error' } });
        setTimeout(() => sessionStorage.removeItem('logoutInProgress'), 1000);
      });
    });

    return () => {
      unsubAuth();
      if (unsubSessions) unsubSessions();
      if (unsubRevocation) unsubRevocation();
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Session handlers ----
  const handleRevokeSession = async (sessionId) => {
    if (revokeLoadingId) return;
    setRevokeLoadingId(sessionId);
    try {
      await apiRevokeSession(sessionId);
    } catch (err) {
      console.error('[MedicalProfile] revokeSession error:', err);
    } finally {
      setRevokeLoadingId(null);
    }
  };

  const handleLogoutAllClick = () => setShowLogoutAllConfirm(true);

  const handleLogoutAllConfirm = async () => {
    setShowLogoutAllConfirm(false);
    setLogoutAllLoading(true);
    try {
      await apiLogoutAllSessions();
      sessionService.clearCurrentSession();
      sessionStorage.setItem('logoutInProgress', 'true');
      await signOut(auth);
      localStorage.clear();
      navigate('/login', { replace: true, state: { message: 'You have been signed out of all sessions.', type: 'success' } });
    } catch (err) {
      console.error('[MedicalProfile] logoutAllSessions error:', err);
      setLogoutAllLoading(false);
    }
  };

  return (
    <div className="psp-main">
      <div className="psp-grid">
        <div className="psp-left">
          <section className="psp-section">
            <h2 className="psp-section-title">{t.accountSettings}</h2>
            <div className="psp-field">
              <label>{t.fullName}</label>
              <input type="text" value={userData.fullName} readOnly />
            </div>
            <div className="psp-field psp-field-with-info">
              <label>{t.email}</label>
              <div className="psp-input-wrap">
                <input type="email" value={userData.email} readOnly />
                <span className="psp-info-icon" title="Info">
                  <FiInfo />
                </span>
              </div>
            </div>
          </section>

          <section className="psp-section">
            <StaffChangePassword userData={userData} onPasswordVerificationRequired={handlePasswordVerificationRequired} />
          </section>
        </div>

        <div className="psp-right">
          <div className="psp-box">
            <div className="psp-section-header">
              <h2 className="psp-section-title">{t.personalInformation}</h2>
              {!isEditingPhone && (
                <button
                  type="button"
                  className="psp-btn-edit"
                  onClick={() => {
                    setIsEditingPhone(true);
                    // Strip +63 prefix if present, keep only last 10 digits
                    const phoneDigits = userData.phone.replace(/\D/g, "").slice(-10);
                    setTempPhone(phoneDigits);
                    setTempLanguage(userData.language);
                  }}
                >
                  {t.editProfile}
                </button>
              )}
            </div>
            <div className="psp-field">
              <label>{t.phoneNumber}</label>
              {isEditingPhone ? (
                <div style={{ display: "flex", width: "100%" }}>
                  <span style={{ 
                    background: "#eff6ff", 
                    padding: "12px 16px", 
                    borderRadius: "12px 0 0 12px", 
                    border: "1px solid #e2e8f0", 
                    borderRight: "none", 
                    color: "#1e40af", 
                    fontWeight: "600" 
                  }}>+63</span>
                  <input
                    type="tel"
                    value={tempPhone}
                    placeholder="9123456789"
                    onChange={(e) => handleContactChange(e.target.value)}
                    style={{ 
                      borderRadius: "0 12px 12px 0", 
                      border: "1px solid #e2e8f0", 
                      borderLeft: "none",
                      flex: 1,
                      padding: "12px 16px"
                    }}
                  />
                </div>
              ) : (
                <input
                  type="tel"
                  value={`+63 ${userData.phone}`}
                  readOnly
                />
              )}
              {errors.contact && isEditingPhone && (
                <p style={{ color: "#dc2626", fontSize: "14px", margin: "4px 0 0", textAlign: 'left' }}>
                  {errors.contact}
                </p>
              )}
            </div>
            <div className="psp-field">
              <label>{t.preferredLanguage}</label>
              {isEditingPhone ? (
                <select value={tempLanguage} onChange={(e) => setTempLanguage(e.target.value)}>
                  <option value="English">{t.english}</option>
                  <option value="Filipino">{t.filipino}</option>
                </select>
              ) : (
                <input type="text" value={userData.language} readOnly />
              )}
            </div>
            {isEditingPhone && (
              <div className="psp-edit-actions">
                <button
                  type="button"
                  className="psp-btn-outline"
                  onClick={() => {
                    setIsEditingPhone(false);
                    // Strip +63 prefix if present, keep only last 10 digits
                    const phoneDigits = userData.phone.replace(/\D/g, "").slice(-10);
                    setTempPhone(phoneDigits);
                    setTempLanguage(userData.language);
                  }}
                >
                  {t.cancel}
                </button>
                <button 
                  type="button" 
                  className="psp-btn-primary" 
                  onClick={async () => {
                    const currentPhoneDigits = userData.phone.replace(/\D/g, "").slice(-10);
                    const tempPhoneDigits = tempPhone.replace(/\D/g, "").slice(-10);
                    
                    if (tempPhoneDigits !== currentPhoneDigits) {
                      // Save pending contact change
                      const contactChangeData = {
                        uid: auth.currentUser?.uid,
                        currentPhone: userData.phone,
                        newPhone: tempPhone,
                        newLanguage: tempLanguage
                      };
                      setPendingContactChange(contactChangeData);
                      localStorage.setItem('pendingStaffContactChange', JSON.stringify(contactChangeData));
                      
                      // Show verification modal
                      setShowContactVerificationModal(true);
                    } else if (tempLanguage !== userData.language) {
                      // Only update language if no phone change - no OTP needed
                      try {
                        await updateDoc(doc(db, "users", auth.currentUser?.uid), {
                          language: tempLanguage,
                          updatedAt: serverTimestamp()
                        });
                        // Update language in context
                        await changeLanguage(tempLanguage === "Filipino" ? "tl" : "en");
                        setUserData({...userData, language: tempLanguage});
                        setIsEditingPhone(false);
                      } catch (error) {
                        console.error("Error updating language:", error);
                        alert("Failed to update language. Please try again.");
                      }
                    } else {
                      // No changes
                      setIsEditingPhone(false);
                    }
                  }}
                  disabled={!!errors.contact || !tempPhone.trim() || (tempPhone === userData.phone.replace(/\D/g, "").slice(-10) && tempLanguage === userData.language)}
                  style={{ opacity: (errors.contact || !tempPhone.trim() || (tempPhone === userData.phone.replace(/\D/g, "").slice(-10) && tempLanguage === userData.language)) ? 0.5 : 1, cursor: (errors.contact || !tempPhone.trim() || (tempPhone === userData.phone.replace(/\D/g, "").slice(-10) && tempLanguage === userData.language)) ? 'not-allowed' : 'pointer' }}
                >
                  {t.saveChanges}
                </button>
              </div>
            )}
          </div>

          <div className="psp-box">
            <h2 className="psp-section-title">Access & Security</h2>
            <div className="psp-toggle-row">
              <div>
                <div className="psp-toggle-label">Two-Factor Authentication</div>
                <div className="psp-toggle-desc" style={{ textAlign: 'left' }}>Manage how your account is verified during login using one-time password (OTP) authentication.</div>
              </div>
              <button
                type="button"
                className={`psp-toggle ${twoFactorEnabled ? "psp-toggle-on" : ""}`}
                onClick={() => {
                  setPendingTwoFactorValue(!twoFactorEnabled);
                  setShowTwoFactorModal(true);
                }}
                aria-label="Toggle two-factor authentication"
              >
                <span className="psp-toggle-knob" />
              </button>
            </div>
            {/* Account Information */}
            <div className="psp-account-info">
              <div className="psp-account-info-header">
                <h3 className="psp-account-info-title">{t.accountSettings}</h3>
              </div>
              <div className="psp-row-fields">
                <div className="psp-field">
                  <label>{t.dateCreated}</label>
                  <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                    <div>{userData.dateCreated.date}</div>
                    <div style={{ fontSize: '0.85em', color: '#64748b' }}>{userData.dateCreated.time}</div>
                  </div>
                </div>
                <div className="psp-field">
                  <label>{t.lastLogin}</label>
                  <div className="psp-field-value" style={{ lineHeight: '1.5' }}>
                    <div>{userData.lastLogin.date}</div>
                    <div style={{ fontSize: '0.85em', color: '#64748b' }}>{userData.lastLogin.time}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="psp-sessions">
              <div className="psp-sessions-header">
                <div className="psp-sessions-label">Where You're Logged In</div>
                <button
                  type="button"
                  className="psp-btn-revoke psp-btn-logout-all"
                  style={{ backgroundColor: 'transparent', borderColor: '#dc2626', color: '#dc2626' }}
                  onClick={handleLogoutAllClick}
                  disabled={logoutAllLoading}
                >
                  {logoutAllLoading ? "Signing out…" : "Log Out All Sessions"}
                </button>
              </div>

              {showLogoutAllConfirm && (
                <div className="psp-session-confirm-overlay" role="dialog" aria-modal="true" aria-label="Confirm log out all sessions">
                  <div className="psp-session-confirm-box">
                    <p>This will sign you out of <strong>all devices</strong>. Continue?</p>
                    <div className="psp-session-confirm-actions" style={{ justifyContent: 'center' }}>
                      <button
                        type="button"
                        className="psp-btn-revoke"
                        style={{ borderColor: '#2563eb', color: '#2563eb', backgroundColor: 'transparent' }}
                        onClick={() => setShowLogoutAllConfirm(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="psp-btn-revoke"
                        style={{ borderColor: '#dc2626', color: '#dc2626', backgroundColor: 'transparent' }}
                        onClick={handleLogoutAllConfirm}
                      >
                        Yes, log out all
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {sessionsLoading ? (
                <p style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>Loading sessions…</p>
              ) : activeSessions.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#64748b', padding: '8px 0' }}>No active sessions found.</p>
              ) : (
                <ul className="psp-session-list" style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                  {activeSessions.map((s) => {
                    const isCurrent = s.sessionId === currentSessionId;
                    const isRevoking = revokeLoadingId === s.sessionId;
                    const lastActive = s.lastActiveAt?.toDate?.() ?? null;
                    const lastActiveLabel = lastActive ? formatSessionTime(lastActive) : "Unknown";
                    return (
                      <li key={s.sessionId} className={`psp-session-item${isCurrent ? " psp-session-item--current" : ""}`}>
                        <div className="psp-session-info">
                          <span className="psp-session-device">
                            {s.deviceName || `${s.browser || "Unknown"} on ${s.operatingSystem || "Unknown"}`}
                            {isCurrent && (
                              <span style={{ marginLeft: '8px', fontSize: '0.72rem', background: '#dcfce7', color: '#15803d', borderRadius: '4px', padding: '1px 6px', fontWeight: 600 }}>
                                Current session
                              </span>
                            )}
                          </span>
                          <span className="psp-session-location" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            {s.deviceType || "Desktop"} · Last active: {lastActiveLabel}
                          </span>
                        </div>
                        {!isCurrent && (
                          <button
                            type="button"
                            className="psp-btn-revoke"
                            onClick={() => handleRevokeSession(s.sessionId)}
                            disabled={isRevoking || logoutAllLoading}
                            aria-label={`Revoke session on ${s.deviceName}`}
                          >
                            {isRevoking ? "Revoking…" : "Revoke"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <TwoFactorOtpModal
        isOpen={showTwoFactorModal}
        onClose={() => setShowTwoFactorModal(false)}
        targetValue={pendingTwoFactorValue}
        onSuccess={handleTwoFactorVerificationSuccess}
        userEmail={userData.email}
        userPhone={userData.phone}
        userFirstName={userData.fullName}
      />

      <StaffChangeVerificationModal
        isOpen={showContactVerificationModal}
        onClose={() => setShowContactVerificationModal(false)}
        flow="contact-change"
        pendingContactChange={pendingContactChange}
        onSuccess={handleContactVerificationSuccess}
        onCancel={() => {
          setShowContactVerificationModal(false);
          setIsEditingPhone(false);
        }}
      />

      <StaffChangeVerificationModal
        isOpen={showPasswordVerificationModal}
        onClose={() => setShowPasswordVerificationModal(false)}
        flow="password-change"
        pendingPasswordChange={pendingPasswordChange}
        onSuccess={handlePasswordVerificationSuccess}
        onCancel={() => {
          setShowPasswordVerificationModal(false);
        }}
      />
    </div>
  );
};

export default MedicalProfile;
